import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Sun, TrendingDown, Leaf, Clock, PanelsTopLeft, TreePine, Zap, Plus, Minus, FileDown, Loader2, Gauge, ArrowUpRight, Power } from "lucide-react";
import { Link } from "react-router-dom";
import { APPLIANCES, calculateFromAppliances, fmtTL, fmtNum } from "../lib/calc";
import { generateOfferPDF } from "../lib/pdf";
import { toast } from "sonner";

const ResultCard = ({ icon: Icon, label, value, unit, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`result-${label}`}
    className={`relative p-6 rounded-2xl border ${
      accent ? "bg-[#5CC528]/[0.08] border-[#5CC528]/30" : "bg-[#121212] border-white/10"
    } overflow-hidden`}
  >
    <Icon size={20} className={accent ? "text-[#5CC528]" : "text-neutral-500"} />
    <div className="mt-4">
      <div className={`font-display font-bold tracking-tight text-2xl sm:text-3xl ${accent ? "text-[#5CC528]" : "text-white"}`}>
        {value}
        {unit && <span className="text-sm font-normal text-neutral-500 ml-1">{unit}</span>}
      </div>
      <div className="text-xs text-neutral-400 mt-1.5 leading-snug">{label}</div>
    </div>
  </motion.div>
);

const Stepper = ({ value, onChange, min = 0, max = 99, testid }) => (
  <div className="flex items-center gap-1.5">
    <button
      type="button"
      data-testid={`${testid}-minus`}
      onClick={() => onChange(Math.max(min, value - 1))}
      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-[#5CC528] hover:text-black text-neutral-300 flex items-center justify-center transition-colors duration-200"
    >
      <Minus size={15} />
    </button>
    <span data-testid={`${testid}-value`} className="w-8 text-center font-display font-semibold text-white tabular-nums">{value}</span>
    <button
      type="button"
      data-testid={`${testid}-plus`}
      onClick={() => onChange(Math.min(max, value + 1))}
      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-[#5CC528] hover:text-black text-neutral-300 flex items-center justify-center transition-colors duration-200"
    >
      <Plus size={15} />
    </button>
  </div>
);

export const SolarCalculator = () => {
  const [items, setItems] = useState(
    APPLIANCES.map((a) => ({ ...a, enabled: a.id === "buzdolabi" || a.id === "aydinlatma" }))
  );
  const [customer, setCustomer] = useState({ name: "", phone: "" });
  const [generating, setGenerating] = useState(false);

  const activeItems = useMemo(() => items.filter((i) => i.enabled), [items]);
  const r = useMemo(
    () => calculateFromAppliances(activeItems.map((a) => ({ watt: a.watt, maxWatt: a.maxWatt, id: a.id, qty: a.qty, hours: a.hours }))),
    [activeItems]
  );

  const setField = (id, key, val) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, [key]: val } : i)));

  const toggle = (id) =>
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, enabled: !i.enabled } : i)));

  const downloadPDF = async () => {
    if (activeItems.length === 0) {
      toast.error("Lütfen en az bir cihaz ekleyin.");
      return;
    }
    setGenerating(true);
    try {
      await generateOfferPDF({ consumption: r, results: r, customer });
      toast.success("Teklif PDF'i indirildi.");
    } catch (e) {
      toast.error("PDF oluşturulamadı, tekrar deneyin.");
    } finally {
      setGenerating(false);
    }
  };

  const inputCls =
    "w-16 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-center text-white text-sm focus:border-[#5CC528] focus:outline-none";

  return (
    <div data-testid="solar-calculator" className="relative">
      {/* Appliance inputs */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[#5CC528] text-xs uppercase tracking-[0.2em] mb-5">
          <Zap size={14} /> Evinizdeki Cihazları Seçin
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.id}
                data-testid={`appliance-${a.id}`}
                className={`p-5 rounded-2xl border transition-colors duration-200 ${
                  a.enabled ? "bg-[#121212] border-[#5CC528]/30" : "bg-[#0f0f0f] border-white/10"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => toggle(a.id)}
                    data-testid={`appliance-toggle-${a.id}`}
                    className="flex items-center gap-3 min-w-0"
                  >
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors duration-200 ${a.enabled ? "bg-[#5CC528] text-black" : "bg-white/5 text-neutral-500"}`}>
                      <Icon size={19} />
                    </span>
                    <span className={`font-display font-medium text-left ${a.enabled ? "text-white" : "text-neutral-500"}`}>{a.name}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggle(a.id)}
                    className={`shrink-0 text-xs px-3 py-1.5 rounded-full transition-colors duration-200 ${a.enabled ? "bg-[#5CC528]/15 text-[#5CC528]" : "bg-white/5 text-neutral-400 hover:text-white"}`}
                  >
                    {a.enabled ? "Ekli" : "Ekle"}
                  </button>
                </div>

                {a.enabled && (
                  <div className="mt-4 flex items-center justify-between gap-4 pt-4 border-t border-white/5">
                    <div>
                      <div className="text-[11px] text-neutral-500 mb-1.5">Adet</div>
                      <Stepper value={a.qty} onChange={(v) => setField(a.id, "qty", v)} min={1} testid={`qty-${a.id}`} />
                    </div>
                    <div>
                      <div className="text-[11px] text-neutral-500 mb-1.5">Saat / gün</div>
                      {a.id === "buzdolabi" ? (
                        <div
                          data-testid={`hours-${a.id}`}
                          className="w-16 bg-[#0A0A0A] border border-white/10 rounded-lg px-2 py-1.5 text-center text-neutral-400 text-sm cursor-not-allowed"
                          title="Buzdolabı kesintisiz çalışır"
                        >
                          24
                        </div>
                      ) : (
                        <input
                          type="number"
                          min={0}
                          max={24}
                          step={0.5}
                          value={a.hours}
                          data-testid={`hours-${a.id}`}
                          onChange={(e) => setField(a.id, "hours", Math.max(0, Math.min(24, Number(e.target.value) || 0)))}
                          className={inputCls}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Consumption summary bar */}
      <div className="mb-6 p-5 rounded-2xl bg-gradient-to-r from-[#5CC528]/[0.1] to-transparent border border-[#5CC528]/20 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2 text-[#5CC528]"><Gauge size={18} /><span className="text-xs uppercase tracking-[0.15em]">Tahmini Tüketim</span></div>
        <div><span className="font-display font-bold text-2xl text-white">{fmtNum(r.dailyKwh, 1)}</span><span className="text-sm text-neutral-500 ml-1">kWh/gün</span></div>
        <div><span className="font-display font-bold text-2xl text-white">{fmtNum(r.monthlyKwh)}</span><span className="text-sm text-neutral-500 ml-1">kWh/ay</span></div>
        <div><span className="font-display font-bold text-2xl text-[#5CC528]">{fmtTL(r.monthlyBill)}</span><span className="text-sm text-neutral-500 ml-1">tahmini aylık fatura</span></div>
      </div>

      {/* Results */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <ResultCard icon={Sun} label="Önerilen Sistem Gücü" value={fmtNum(r.systemKwp, 1)} unit="kWp" accent delay={0.05} />
        <ResultCard icon={PanelsTopLeft} label="Gerekli Panel Sayısı" value={fmtNum(r.panelCount)} unit="adet" delay={0.1} />
        <ResultCard icon={Power} label="Gerekli İnverter Gücü" value={fmtNum(r.inverterKw, 1)} unit="kW" accent delay={0.15} />
        <ResultCard icon={Zap} label="Yıllık Üretim" value={fmtNum(r.yearlyProduction)} unit="kWh" delay={0.2} />
        <ResultCard icon={TrendingDown} label="Yıllık Tasarruf" value={fmtTL(r.yearlySavings)} accent delay={0.25} />
        <ResultCard icon={Clock} label="Yatırım Geri Dönüşü" value={fmtNum(r.paybackYears, 1)} unit="yıl" delay={0.3} />
        <Link
          to="/iletisim"
          data-testid="get-quote-btn"
          className="group relative p-6 rounded-2xl bg-[#5CC528] text-black overflow-hidden flex flex-col justify-between hover:bg-[#4AA020] transition-colors duration-200"
        >
          <ArrowUpRight size={22} className="self-end transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          <div>
            <div className="font-display font-bold text-2xl sm:text-3xl tracking-tight leading-none">Fiyat Teklifi Alın</div>
            <div className="text-xs mt-2 opacity-80">Size özel net fiyat için ücretsiz keşif</div>
          </div>
        </Link>
      </div>

      {/* Environmental impact */}
      <div className="mt-4 p-8 rounded-2xl bg-gradient-to-br from-[#5CC528]/[0.12] to-transparent border border-[#5CC528]/20">
        <div className="flex items-center gap-2 text-[#5CC528] text-xs uppercase tracking-[0.2em] mb-6">
          <Leaf size={14} /> Çevresel Etki
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div>
            <div className="font-display font-bold text-3xl text-white">{fmtNum(r.co2Yearly, 1)} <span className="text-sm text-neutral-400">ton/yıl</span></div>
            <div className="text-xs text-neutral-400 mt-1">Yıllık CO₂ tasarrufu</div>
          </div>
          <div>
            <div className="font-display font-bold text-3xl text-white">{fmtNum(r.co2Lifetime, 0)} <span className="text-sm text-neutral-400">ton</span></div>
            <div className="text-xs text-neutral-400 mt-1">25 yıllık toplam CO₂</div>
          </div>
          <div className="flex items-start gap-3">
            <TreePine size={28} className="text-[#5CC528] shrink-0" />
            <div>
              <div className="font-display font-bold text-3xl text-white">{fmtNum(r.treesEquivalent)}</div>
              <div className="text-xs text-neutral-400 mt-1">ağaca eşdeğer (yıllık)</div>
            </div>
          </div>
        </div>
      </div>

      {/* PDF export */}
      <div className="mt-6 p-8 rounded-2xl bg-[#121212] border border-white/10">
        <div className="flex items-center gap-2 text-[#5CC528] text-xs uppercase tracking-[0.2em] mb-5">
          <FileDown size={14} /> Teklifinizi PDF İndirin
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-sm text-neutral-400 mb-2 block">Ad Soyad</label>
            <input
              data-testid="pdf-name"
              value={customer.name}
              onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))}
              placeholder="Adınız"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:border-[#5CC528] focus:outline-none"
            />
          </div>
          <div>
            <label className="text-sm text-neutral-400 mb-2 block">Telefon</label>
            <input
              data-testid="pdf-phone"
              value={customer.phone}
              onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))}
              placeholder="05xx xxx xx xx"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:border-[#5CC528] focus:outline-none"
            />
          </div>
          <button
            onClick={downloadPDF}
            disabled={generating}
            data-testid="download-pdf-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#5CC528] text-black font-semibold px-6 py-3 rounded-xl hover:bg-[#4AA020] transition-colors duration-200 disabled:opacity-60"
          >
            {generating ? <><Loader2 size={17} className="animate-spin" /> Hazırlanıyor</> : <><FileDown size={17} /> Teklif PDF İndir</>}
          </button>
        </div>
      </div>

      <p className="mt-5 text-xs text-neutral-500 leading-relaxed">
        * Sonuçlar Akdeniz bölgesi güneşlenme değerleri ve tipik cihaz güçleri baz alınarak hesaplanan tahmini
        değerlerdir. Net proje için ücretsiz keşif ve fizibilite talep edebilirsiniz.
      </p>
    </div>
  );
};
